import type { Status, TripDateOrigin } from '../../types';
import { parseDateValue } from '../formatDateWithLocale';

/**
 * Advisory shown in TripForm when the selected status no longer matches the
 * effective trip date range. The server never auto-updates status; the user
 * may accept the suggestion or keep the current value.
 */
export interface StatusWarning {
  currentStatus: Status;
  suggestedStatus: Status;
}

/** True only when end parses to a datetime strictly after now. */
function isEndInFuture(end: string | null): boolean {
  if (!end) {
    return false;
  }
  const parsed = parseDateValue(end);
  if (!parsed) {
    return false;
  }
  return parsed.getTime() > Date.now();
}

/**
 * True when there is no usable end date or the end is strictly before now.
 * Missing/unparseable end is treated as "no future trip window" (past).
 */
function isEndInPast(end: string | null): boolean {
  if (!end) {
    return true;
  }
  const parsed = parseDateValue(end);
  if (!parsed) {
    return true;
  }
  return parsed.getTime() < Date.now();
}

/**
 * Evaluates whether `currentStatus` is inconsistent with the effective date
 * source and end date. Called from TripForm on every render where `formData`
 * or loaded `trip` changes — not only after an explicit edit.
 *
 * @param currentStatus - Status selected in the form (`formData.status`).
 * @param newSource - Effective date origin for this evaluation:
 *   `'locked'` when the lock toggle is on; otherwise `resolveDates(trip).source`
 *   when editing, or `'none'` on create.
 * @param newEnd - Effective end date wire string: form lock end when lock is on;
 *   otherwise `trip.derivedEndDate` when editing, or `null` on create.
 * @returns A suggestion when status looks stale, or `null` when consistent.
 *
 * PLANNING is never flagged — it is the default/ambiguous state. COMPLETED,
 * ACTIVE, and CANCELLED are checked against the date window below.
 */
export function deriveStatusWarning(
  currentStatus: Status,
  newSource: TripDateOrigin,
  newEnd: string | null
): StatusWarning | null {
  // Trip marked done but still has a future end → likely still in progress.
  if (currentStatus === 'COMPLETED' && isEndInFuture(newEnd)) {
    return { currentStatus, suggestedStatus: 'ACTIVE' };
  }

  // ACTIVE implies an ongoing or upcoming window. No dates (`none`) or a past
  // end means there is nothing "active" left to track.
  if (
    currentStatus === 'ACTIVE' &&
    (newSource === 'none' || isEndInPast(newEnd))
  ) {
    return { currentStatus, suggestedStatus: 'PLANNING' };
  }

  // Cancelled trips with a future end may be worth reopening for planning.
  if (currentStatus === 'CANCELLED' && isEndInFuture(newEnd)) {
    return { currentStatus, suggestedStatus: 'PLANNING' };
  }

  return null;
}
